import React from "react";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import { num, pct, share } from "./format";

// higherIsBetter decides whether an increase is shown green or red
const Delta = ({ now, before, higherIsBetter = true, points = false, label }) => {
  if (before === undefined || before === null) return null;
  const diff = now - before;
  if (!before && !points) return <span className="a2-delta-flat">no data {label}</span>;
  if (Math.abs(diff) < 0.05) return <span className="a2-delta-flat">no change {label}</span>;
  const good = diff > 0 === higherIsBetter;
  return (
    <span className={good ? "a2-delta-up" : "a2-delta-down"}>
      {diff > 0 ? "▲" : "▼"} {points ? `${Math.abs(diff).toFixed(1)} pts` : num(Math.abs(diff))} {label}
    </span>
  );
};

const Kpi = ({ title, icon, tone, value, meter, foot, delta, onClick }) => (
  <div className="a2-card a2-kpi" onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
    <div className="a2-kpi-top">
      {title}
      <span className={`a2-kpi-icon bg-${tone}`}>{icon}</span>
    </div>
    <div className="a2-kpi-value">{value}</div>
    <div className="a2-meter">
      <span className={`fill-${tone}`} style={{ width: `${Math.min(100, Math.max(0, meter || 0))}%` }} />
    </div>
    <div className="a2-kpi-foot">
      <span>{foot}</span>
      {delta}
    </div>
  </div>
);

const KpiCards = ({ summary, threshold, onPick }) => {
  if (!summary) return <div className="a2-kpis">{Array.from({ length: 9 }, (_, i) => <div key={i} className="a2-card a2-kpi a2-loading" />)}</div>;
  const c = summary.current;
  const p = summary.previous;
  const cov = summary.coverage || {};
  const vs = summary.day_count === 1 ? "vs prev day" : "vs prev period";
  const present = c.present + c.working;
  const expected = c.employee_days - c.weekoff;

  return (
    <div className="a2-kpis">
      <Kpi
        title="Total Employees" tone="indigo" icon={<GroupOutlinedIcon />}
        value={num(c.employees)} meter={100} foot={`${num(cov.clients)} clients`}
        delta={<Delta now={c.employees} before={p.employees} label={vs} />}
        onClick={() => onPick("all")}
      />
      <Kpi
        title="Total Manpower" tone="indigo" icon={<BadgeOutlinedIcon />}
        value={c.total_manpower === null ? "—" : c.total_manpower ? num(c.total_manpower) : "Not set"}
        meter={c.total_manpower ? share(present, c.total_manpower * summary.day_count) : 0}
        foot={
          c.total_manpower === null
            ? "Not available with this filter"
            : `${num(c.manpower_not_set)} locations not set`
        }
      />
      <Kpi
        title="Present" tone="green" icon={<HowToRegOutlinedIcon />}
        value={num(present)} meter={share(present, expected)}
        foot={`${pct(share(present, expected))} of rostered`}
        delta={<Delta now={present} before={p.present + p.working} label={vs} />}
        onClick={() => onPick("present")}
      />
      <Kpi
        title="Absent" tone="red" icon={<PersonOffOutlinedIcon />}
        value={num(c.absent)} meter={share(c.absent, c.employee_days)}
        foot={`${pct(share(c.absent, c.employee_days))} of workforce`}
        delta={<Delta now={c.absent} before={p.absent} higherIsBetter={false} label={vs} />}
        onClick={() => onPick("absent")}
      />
      <Kpi
        title="On Leave" tone="amber" icon={<EventNoteOutlinedIcon />}
        value={num(c.leave)} meter={share(c.leave, c.employee_days)}
        foot={`${pct(share(c.leave, c.employee_days))} of workforce`}
        delta={<Delta now={c.leave} before={p.leave} higherIsBetter={false} label={vs} />}
        onClick={() => onPick("leave")}
      />
      <Kpi
        title="Punched In" tone="blue" icon={<LoginOutlinedIcon />}
        value={num(c.punched_in)} meter={share(c.punched_in, expected)}
        foot={`${pct(share(c.punched_in, expected))} of rostered`}
        delta={<Delta now={c.punched_in} before={p.punched_in} label={vs} />}
        onClick={() => onPick("punched_in")}
      />
      <Kpi
        title="Not Punched In" tone="grey" icon={<RemoveCircleOutlineIcon />}
        value={num(c.not_punched_in)} meter={share(c.not_punched_in, expected)}
        foot="Rostered, no punch"
        delta={<Delta now={c.not_punched_in} before={p.not_punched_in} higherIsBetter={false} label={vs} />}
        onClick={() => onPick("not_punched_in")}
      />
      <Kpi
        title="Client Locations" tone="indigo" icon={<ApartmentOutlinedIcon />}
        value={num(cov.locations)} meter={100}
        foot={`${num(cov.clients)} clients · ${num(cov.zones)} zones`}
      />
      <Kpi
        title="Attendance %" tone={c.attendance_pct < threshold ? "red" : "green"} icon={<PercentOutlinedIcon />}
        value={pct(c.attendance_pct)} meter={c.attendance_pct}
        foot={c.total_manpower ? `Present ÷ manpower · threshold ${threshold}%` : `Threshold ${threshold}%`}
        delta={<Delta now={c.attendance_pct} before={p.employee_days ? p.attendance_pct : null} points label={vs} />}
      />
    </div>
  );
};

export default KpiCards;
